import { anyToNumber, useRouter } from '@application/utils';
import { EducatorTypeEnum } from '@gql/graphql';
import { useCallback, useMemo } from 'react';
import type { ProListPageControllerFormValues } from '../..';
import { EDUCATOR_MAP_INITIAL_VALUES, PROS_MAP_INITIAL_VALUES } from './constants';

export const useSearch = () => {
  const { infoFromCurrentPath, setCurrentSearch, countryCode } = useRouter();

  const proMapInitialValues = useMemo(() => {
    if (infoFromCurrentPath.route === 'educatorList') {
      return EDUCATOR_MAP_INITIAL_VALUES[countryCode];
    }
    return PROS_MAP_INITIAL_VALUES[countryCode];
  }, [infoFromCurrentPath.route, countryCode]);

  const formatEducatorTypes = useCallback((educatorTypes: EducatorTypeEnum[]) => {
    if (!educatorTypes?.length) return;
    if (
      (educatorTypes.includes(EducatorTypeEnum.EDUCATOR) ||
        educatorTypes.includes(EducatorTypeEnum.BEHAVIORIST)) &&
      !educatorTypes.includes(EducatorTypeEnum.EDUCATOR_AND_BEHAVIORIST)
    ) {
      return [...educatorTypes, EducatorTypeEnum.EDUCATOR_AND_BEHAVIORIST];
    }
    return educatorTypes;
  }, []);

  const getSearchFromValues = useCallback(
    (values: ProListPageControllerFormValues) => {
      const { page } = infoFromCurrentPath.search ?? {};
      return {
        page,
        maxDistance: values.maxDistance,
        zoom: values.zoom,
        lat: values.mapCenter.lat,
        lng: values.mapCenter.lng,
        ...(values.hasBooking && { hasBooking: values.hasBooking }),
        ...(values.spokenLanguages?.length && { spokenLanguages: values.spokenLanguages }),
        ...(values.educatorTypes?.length && {
          educatorType: { in: formatEducatorTypes(values.educatorTypes) },
        }),
      };
    },
    [formatEducatorTypes, infoFromCurrentPath.search],
  );

  const setSearchFromValues = useCallback(
    (values: ProListPageControllerFormValues) => {
      setCurrentSearch(getSearchFromValues(values));
    },
    [getSearchFromValues, setCurrentSearch],
  );

  const valuesFromSearch = useMemo((): Partial<ProListPageControllerFormValues> => {
    const { maxDistance, educatorTypes, hasBooking, zoom, lat, lng, spokenLanguages } =
      infoFromCurrentPath.search ?? {};

    return {
      maxDistance: anyToNumber(maxDistance, proMapInitialValues.maxDistance) as number,
      zoom: anyToNumber(zoom, proMapInitialValues.zoom) as number,
      hasBooking: proMapInitialValues.hasBooking,
      ...(hasBooking === 'true' && { hasBooking: true }),
      ...(hasBooking === 'false' && { hasBooking: false }),
      mapCenter: {
        lat: anyToNumber(lat, proMapInitialValues.mapCenter.lat) as number,
        lng: anyToNumber(lng, proMapInitialValues.mapCenter.lng) as number,
      },
      spokenLanguages: (spokenLanguages as string[]) ?? proMapInitialValues.spokenLanguages,
      educatorTypes:
        formatEducatorTypes(educatorTypes as EducatorTypeEnum[]) ??
        proMapInitialValues.educatorTypes,
    };
  }, [
    infoFromCurrentPath.search,
    proMapInitialValues.maxDistance,
    proMapInitialValues.zoom,
    proMapInitialValues.hasBooking,
    proMapInitialValues.mapCenter.lat,
    proMapInitialValues.mapCenter.lng,
    proMapInitialValues.spokenLanguages,
    proMapInitialValues.educatorTypes,
    formatEducatorTypes,
  ]);

  return { setSearchFromValues, valuesFromSearch, getSearchFromValues, formatEducatorTypes };
};
